import { useState, type FormEvent } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { AuthDomainError, useAuth } from '../lib/auth';

export function Login() {
  const { user, loading, loginWithGoogle, login } = useAuth();
  const navigate = useNavigate();
  const [showEmail, setShowEmail] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  if (!loading && user) return <Navigate to="/" replace />;

  const run = async (fn: () => Promise<void>, fallbackMsg: string) => {
    setBusy(true);
    setError('');
    try {
      await fn();
      navigate('/');
    } catch (e) {
      if (e instanceof AuthDomainError) setError(e.message);
      else if ((e as { code?: string }).code === 'auth/popup-closed-by-user') setError('');
      else setError(fallbackMsg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
      <div className="card p-4 shadow-sm" style={{ width: 360 }}>
        <h4 className="text-green fw-bold mb-3">HRLabs Admin</h4>
        {error && <div className="alert alert-warning py-2">{error}</div>}

        <button
          className="btn btn-outline-secondary w-100"
          disabled={busy}
          onClick={() => run(loginWithGoogle, 'Could not sign in with Google. Try again.')}
          type="button"
        >
          {busy ? 'Signing in…' : 'Sign in with Google'}
        </button>

        {!showEmail ? (
          <button
            className="btn btn-link btn-sm mt-2 text-muted"
            onClick={() => setShowEmail(true)}
            type="button"
          >
            Use staff email &amp; password
          </button>
        ) : (
          <form
            className="mt-3"
            onSubmit={(e: FormEvent) => {
              e.preventDefault();
              void run(() => login(email.trim(), password), 'Invalid email or password.');
            }}
          >
            <div className="mb-3">
              <label className="form-label">Email</label>
              <input
                type="email"
                className="form-control"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Password</label>
              <input
                type="password"
                className="form-control"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            <button className="btn btn-success w-100" disabled={busy} type="submit">
              {busy ? 'Signing in…' : 'Sign in'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
